import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Truck, Sparkles } from 'lucide-react';
import { useCart } from '../context/CartContext';

const THRESHOLD = 300;

// A dynamic, site-wide free-shipping progress bar. Appears on every customer page
// once the bag has items, sticks to the top on scroll, and animates as the total grows.
const GlobalShipBar = () => {
  const { subtotal, discount, count } = useCart();
  const { pathname } = useLocation();

  const hidden = count === 0 || ['/cart', '/checkout', '/admin', '/payment'].some((p) => pathname.startsWith(p));

  const net = Math.max(subtotal - discount, 0);
  const remaining = Math.max(THRESHOLD - net, 0);
  const pct = Math.min((net / THRESHOLD) * 100, 100);
  const unlocked = net >= THRESHOLD;
  const almost = !unlocked && remaining <= 60;

  return (
    <AnimatePresence>
      {!hidden && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          className="sticky top-0 z-[45] bg-[#0b0b0b] border-b border-[#c6a15b]/30 overflow-hidden"
          data-testid="global-ship-bar"
        >
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-2.5">
            <div className="flex items-center gap-3">
              <p className="font-body text-[0.72rem] sm:text-xs text-[#e7d3a1] whitespace-nowrap flex items-center gap-1.5">
                {unlocked ? (
                  <>
                    <Sparkles size={14} className="text-[#c6a15b]" />
                    <span data-testid="global-ship-unlocked"><strong>Free U.S. shipping unlocked!</strong> Treat yourself, queen.</span>
                  </>
                ) : (
                  <span>
                    {almost ? 'So close! ' : ''}You're <strong className="text-[#c6a15b]">${remaining.toFixed(2)}</strong> from <strong>FREE shipping</strong>
                  </span>
                )}
              </p>

              <div className="relative flex-1 h-2.5 rounded-full bg-white/10 overflow-visible">
                <motion.div
                  className="ship-fill absolute inset-y-0 left-0 rounded-full"
                  initial={false}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                />
                {/* Truck rides the leading edge of the fill */}
                <motion.div
                  className="absolute -top-1 flex items-center justify-center"
                  initial={false}
                  animate={{ left: `calc(${pct}% - 10px)` }}
                  transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                >
                  <span className={`w-5 h-5 rounded-full bg-[#0b0b0b] border border-[#c6a15b] flex items-center justify-center ${almost ? 'animate-pulse' : ''}`}>
                    {unlocked ? <Sparkles size={11} className="text-[#c6a15b]" /> : <Truck size={11} className="text-[#c6a15b]" />}
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlobalShipBar;
